import * as React from 'react'
import { elementsLockService } from '@/api/services/elements-lock.service'
import type { ElementLayer } from '@/api/types/stowed-element'
import { logger } from '@/utils/logger'

type UseElementsLockOptions = {
  enabled?: boolean
  unlockIdleMs?: number
  beforeUnlock?: () => Promise<void> | void
}

function normalizeIds(ids: string[] | null | undefined): string[] {
  if (!ids || ids.length === 0) return []
  return ids.filter((id): id is string => id.length > 0)
}

/**
 * Advisory lock strategy (fire-and-forget):
 * - `markEdited()` sends a one-time lock notification and schedules an idle unlock.
 * - `release()` sends an unlock immediately.
 *
 * Both lock and unlock are best-effort — failures are logged but never block the UI.
 * The locked set is tracked so we always unlock the exact IDs we previously locked.
 */
export function useElementsLock(
  spreadId: string,
  layer: ElementLayer,
  elementObjectIds: string[] | null | undefined,
  options?: UseElementsLockOptions
) {
  const enabled = options?.enabled ?? true
  const unlockIdleMs = options?.unlockIdleMs ?? 2000
  const beforeUnlock = options?.beforeUnlock

  const ids = React.useMemo(() => normalizeIds(elementObjectIds), [elementObjectIds])
  const idsKey = React.useMemo(() => ids.join(','), [ids])

  const idsRef = React.useRef<string[]>(ids)
  const lockedIdsRef = React.useRef<string[]>([])
  const lockedRef = React.useRef(false)
  const lockInFlightRef = React.useRef<Promise<void> | null>(null)
  const unlockInFlightRef = React.useRef<Promise<void> | null>(null)
  const unlockTimerRef = React.useRef<number | null>(null)
  const spreadIdRef = React.useRef(spreadId)
  const layerRef = React.useRef(layer)
  const beforeUnlockRef = React.useRef(beforeUnlock)

  React.useEffect(() => {
    idsRef.current = ids
  }, [ids])

  React.useEffect(() => {
    spreadIdRef.current = spreadId
    layerRef.current = layer
  }, [spreadId, layer])

  React.useEffect(() => {
    beforeUnlockRef.current = beforeUnlock
  }, [beforeUnlock])

  const isSameIdSet = React.useCallback((a: string[], b: string[]) => {
    if (a.length !== b.length) return false
    const setA = new Set(a)

    for (const id of b) {
      if (!setA.has(id)) return false
    }

    return true
  }, [])

  const clearUnlockTimer = React.useCallback(() => {
    if (unlockTimerRef.current != null) {
      window.clearTimeout(unlockTimerRef.current)
      unlockTimerRef.current = null
    }
  }, [])

  const unlockCurrent = React.useCallback(async () => {
    clearUnlockTimer()
    if (!enabled) return

    try {
      await lockInFlightRef.current
    } catch {
      // ignore
    }

    if (!lockedRef.current) return
    const idsToUnlock = lockedIdsRef.current

    if (idsToUnlock.length === 0) {
      lockedRef.current = false
      return
    }

    if (unlockInFlightRef.current) return await unlockInFlightRef.current

    unlockInFlightRef.current = (async () => {
      try {
        await beforeUnlockRef.current?.()
        await elementsLockService.unlockElements(spreadIdRef.current, layerRef.current, idsToUnlock)
      } catch (err) {
        logger.warn('Failed to unlock elements:', err)
      } finally {
        lockedRef.current = false
        lockedIdsRef.current = []
        unlockInFlightRef.current = null
      }
    })()

    return await unlockInFlightRef.current
  }, [clearUnlockTimer, enabled])

  const release = React.useCallback(async () => {
    await unlockCurrent()
  }, [unlockCurrent])

  const ensureLocked = React.useCallback(
    async (targetIds?: string[]) => {
      if (!enabled) return
      const currentIds = normalizeIds(targetIds?.length ? targetIds : idsRef.current)

      if (currentIds.length === 0) return
      if (lockedRef.current) {
        if (isSameIdSet(lockedIdsRef.current, currentIds)) return
        await unlockCurrent()
      }

      if (lockInFlightRef.current) return await lockInFlightRef.current

      lockInFlightRef.current = (async () => {
        try {
          const res = await elementsLockService.lockElements(
            spreadIdRef.current,
            layerRef.current,
            currentIds
          )

          lockedRef.current = Boolean(res.locked ?? true)
          if (lockedRef.current) {
            lockedIdsRef.current = currentIds
          }
        } catch (err) {
          logger.warn('Failed to lock elements:', err)
        } finally {
          lockInFlightRef.current = null
        }
      })()

      return await lockInFlightRef.current
    },
    [enabled, isSameIdSet, unlockCurrent]
  )

  const scheduleIdleUnlock = React.useCallback(() => {
    clearUnlockTimer()
    if (!enabled) return
    if (unlockIdleMs <= 0) return
    unlockTimerRef.current = window.setTimeout(() => {
      void release()
    }, unlockIdleMs)
  }, [clearUnlockTimer, enabled, unlockIdleMs, release])

  const markEdited = React.useCallback(
    (targetIds?: string[]) => {
      if (!enabled) return Promise.resolve()
      scheduleIdleUnlock()
      return ensureLocked(targetIds)
    },
    [enabled, ensureLocked, scheduleIdleUnlock]
  )

  // On spread (ID set) change, unlock whatever we previously locked.
  React.useEffect(() => {
    return () => {
      void release()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spreadId, layer])

  return { markEdited, release, ids, idsKey }
}
